import request from "@/http"
import { downloadFile } from "@/util/util"
// ==============================================
// 达标率分析的筛选条件
export interface SuccessRateFilters {
  // 区县
  districtId: string
  districtName?: string
  // 街道
  streetId: string
  streetName?: string
  // 月份
  month: string
  // 部门
  org: string
  orgName?: string
  // 关键字
  query: string
}

// 区域范围
export interface AreaCoverageVO {
  // 区县
  districtId: string
  districtName: string
  // 街道
  streetId: string
  streetName: string
}

interface CrmStoreArgs {
  // 城市id
  cityId: string
  // 品牌id
  brandId: string
}
export interface CRMStoreVO {
  employeeName: string
  org: string
  poiId: string
  poiName: string
  address: string
  longitude: number
  latitude: number
  enterTime: string
  exitTime: string
  stayTime: number
  visitCount: number
  // 客流
  uv: number
  hourly: number[]
  cooStatus: boolean
}

/**
 * 获取合作门店
 */
export async function getCrmStore(params: CrmStoreArgs): Promise<CRMStoreVO[]> {
  try {
    const result = await request.get<CRMStoreVO[]>("/v1/industry/crm/store", {
      params: {
        ...params,
      },
    })
    return result?.data
  } catch (error) {
    throw error
  }
}

export interface Store {
  poiId: string
  poiName: string
  address: string
  longitude: number
  latitude: number
  lastEnterTime: string
  lastExitTime: string
  lastDuration: number
  visitCount: number
  cooStatus: boolean
}

export interface TrailDetail {
  // 设备ID
  assetId: string
  // 员工username
  employeeUsername: string
  // 员工姓名
  employeeName: string
  // 员工所属组织
  org: string
  // 门店达标率
  storeRatio: number
  // 拜访时长达标率
  durationRatio: number
  // 拜访频次达标率
  visitCountRatio: number
  // 门店平均拜访时长
  averageDuration: number
  // 店铺
  stores: Store[]
}

export interface TrailVO {
  // 门店达标率
  storeRatio: number
  // 拜访时长达标率
  durationRatio: number
  // 拜访频次达标率
  visitCountRatio: number
  // 门店平均拜访时长
  averageDuration: number
  // 路线
  trailDetails: TrailDetail[]
  // 总数
  total: number
}

interface TrailArgs {
  cityId: string
  brandId: string
  org: string
  month: string
  districtId: string
  streetId: string
  query: string
  // 排序方式（TrailArgsSort）
  sort?: string
  page?: number
  limit?: number
}

/**
 * 获取轨迹
 */
export async function getCrmTrail(params: TrailArgs): Promise<TrailVO> {
  try {
    const result = await request.post<TrailVO>("/v1/industry/crm/trail", params)
    return result?.data
  } catch (error) {
    throw error
  }
}

export interface CRMStoreDetailVO {
  assetId: string
  org: string
  // 额定门店数
  targetCount: number
  // 实际门店数
  actualCount: number
  // 非额定门店数
  missedCount: number
  // 达标率
  ratio: number
}

interface CRMStoreDetailResult {
  list: CRMStoreDetailVO[]
  total: number
}

/**
 * 获取门店达标率
 */
export async function getCrmStoreDetail(params: TrailArgs): Promise<CRMStoreDetailResult> {
  try {
    const result = await request.post<CRMStoreDetailResult>("/v1/industry/crm/storeDetail", params)
    return result?.data
  } catch (error) {
    throw error
  }
}

export interface CRMDurationDetailVO {
  assetId: string
  org: string
  // 额定门店数
  targetCount: number
  // 实际门店数
  actualCount: number
  // 总停留时长
  totalDuration: number
  // 平均停留时长
  averageDuration: number
  // 最长停留时长
  maxDuration: number
  // 最短停留时长
  minDuration: number
  // 达标率
  ratio: number
}

interface CRMDurationDetailResult {
  list: CRMDurationDetailVO[]
  total: number
}

/**
 * 获取拜访时长达标率
 */
export async function getCrmDurationDetail(params: TrailArgs): Promise<CRMDurationDetailResult> {
  try {
    const result = await request.post<CRMDurationDetailResult>(
      "/v1/industry/crm/durationDetail",
      params,
    )
    return result?.data
  } catch (error) {
    throw error
  }
}

export interface CRMVisitCountVO {
  assetId: string
  org: string
  // 额定门店数
  targetCount: number
  // 实际门店数
  actualCount: number
  // 总频次
  totalVisitCount: number
  // 平均次数
  averageVisitCount: number
  // 最多次数
  maxVisitCount: number
  // 最少次数
  minVisitCount: number
  // 达标率
  ratio: number
}

interface CRMVisitCountResult {
  list: CRMVisitCountVO[]
  total: number
}

/**
 * 获取拜访频次达标率
 */
export async function getCrmVisitCountDetail(params: TrailArgs): Promise<CRMVisitCountResult> {
  try {
    const result = await request.post<CRMVisitCountResult>(
      "/v1/industry/crm/visitCountDetail",
      params,
    )
    return result?.data
  } catch (error) {
    throw error
  }
}

export interface CRMStoreAverageTimeVO {
  // 门店编号
  poiId: string
  // 门店名称
  poiName: string
  // 门店地址
  address: string
  // 区域
  districtName: string
  // 类型-1
  type1: string
  // 类型-2
  type2: string
  // 设备编号
  assetId: string
  // 部门
  org: string
  // 拜访次数
  visitCount: number
  // 拜访时间
  totalVisitTime: number
  // 平均拜访时间
  averageVisitTime: number
}

interface CRMStoreAverageTimeResult {
  list: CRMStoreAverageTimeVO[]
  total: number
}

/**
 * 获取门店平均拜访时长
 */
export async function getCrmStoreAverageTime(
  params: TrailArgs,
): Promise<CRMStoreAverageTimeResult> {
  try {
    const result = await request.post<CRMStoreAverageTimeResult>(
      "/v1/industry/crm/storeAverageTime",
      params,
    )
    return result?.data
  } catch (error) {
    throw error
  }
}

/**
 * 导出数据
 */
export async function exportCrmExcel(params: TrailArgs): Promise<any> {
  try {
    const result = await request.post<any>("/v1/industry/crm/export", params, {
      responseType: "blob",
    })
    downloadFile(result.data, `报表分析数据.xlsx`)
    return result.status
  } catch (error) {
    throw error
  }
}
